import { Typography } from '@euler/app/components/typography';
import { campaignStatusColor } from '@euler/app/flows/ai-suite/chat/AiCampaignMonitor';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { kApdmCampaignStatusLabels } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignStatus } from '@euler/model/apdm';
import { LucideIcon } from 'lucide-react-native';
import { MotiView } from 'moti';
import { ReactNode, memo } from 'react';
import {
  DimensionValue,
  Pressable,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

/** The white card of the campaign pages: soft border, soft shadow. */
export const ApdmCard = memo(
  ({
    children,
    style,
    onPress,
    accessibilityLabel,
  }: {
    children: ReactNode;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
    accessibilityLabel?: string;
  }) => {
    const { rpx } = useResponsiveContext();
    const card: ViewStyle = {
      backgroundColor: aiColors.card,
      borderRadius: rpx(14),
      borderWidth: 1,
      borderColor: aiColors.border,
      shadowColor: '#0F172A',
      shadowOpacity: 0.06,
      shadowRadius: 6,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    };
    if (!onPress) return <View style={[card, style]}>{children}</View>;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={onPress}
        style={({ pressed }) => [card, style, pressed && { opacity: 0.85 }]}
      >
        {children}
      </Pressable>
    );
  },
);

/** A campaign's status as a tinted pill with a dot. */
export const ApdmStatusPill = memo(
  ({ status }: { status: ApdmCampaignStatus }) => {
    const { rpx } = useResponsiveContext();
    const color = campaignStatusColor(status);
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(2),
          borderRadius: rpx(10),
          backgroundColor: tint(color, 0.12),
        }}
      >
        <View
          style={{
            width: rpx(6),
            height: rpx(6),
            borderRadius: rpx(3),
            backgroundColor: color,
          }}
        />
        <Typography.Text medium size={rpx(11)} color={color}>
          {kApdmCampaignStatusLabels[status]}
        </Typography.Text>
      </View>
    );
  },
);

/** A small tinted label, e.g. `新线索 3` or a customer's state. */
export const ApdmTag = memo(
  ({
    text,
    color,
    round,
    icon: Icon,
  }: {
    text: string;
    color: string;
    /** A pill rather than a chip. */
    round?: boolean;
    /** A small mark before the text, e.g. a check on a done state. */
    icon?: LucideIcon;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(3),
          paddingHorizontal: rpx(8),
          paddingVertical: round ? rpx(2) : rpx(4),
          borderRadius: round ? rpx(10) : rpx(8),
          backgroundColor: tint(color, 0.1),
        }}
      >
        {Icon ? <Icon size={rpx(11)} color={color} strokeWidth={3} /> : null}
        <Typography.Text medium size={rpx(11)} color={color}>
          {text}
        </Typography.Text>
      </View>
    );
  },
);

/** A tinted square holding an icon. */
export const ApdmIconTile = memo(
  ({
    icon: Icon,
    color,
    size = 32,
  }: {
    icon: LucideIcon;
    color: string;
    size?: number;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          width: rpx(size),
          height: rpx(size),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(color, 0.12),
        }}
      >
        <Icon size={size / 2} color={color} />
      </View>
    );
  },
);

/** A card's title row: a heading and a quiet note on the right. */
export const ApdmSectionTitle = memo(
  ({ title, note }: { title: string; note?: string }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
          {title}
        </Typography.Text>
        {note ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {note}
          </Typography.Text>
        ) : null}
      </View>
    );
  },
);

/** A plate drawn as one: the province on the brand colour, then the rest. */
export const ApdmPlate = memo(({ plate }: { plate: string }) => {
  const { rpx } = useResponsiveContext();
  const province = /^[一-龥]/.test(plate) ? plate.slice(0, 1) : null;
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(2),
        padding: rpx(2),
        borderRadius: rpx(6),
        borderWidth: 1,
        borderColor: tint(aiColors.primary, 0.6),
        backgroundColor: aiColors.card,
      }}
    >
      {province ? (
        <View
          style={{
            paddingHorizontal: rpx(6),
            paddingVertical: rpx(3),
            borderRadius: rpx(5),
            backgroundColor: aiColors.primary,
          }}
        >
          <Typography.Text bold size={rpx(13)} color="#fff">
            {province}
          </Typography.Text>
        </View>
      ) : null}
      <Typography.Text
        bold
        size={rpx(13)}
        color={aiColors.foreground}
        style={{ paddingHorizontal: rpx(6), letterSpacing: rpx(1.5) }}
      >
        {province ? plate.slice(1) : plate}
      </Typography.Text>
    </View>
  );
});

/** One small fact at the foot of a row: an icon and a few words. */
export interface ApdmFact {
  key: string;
  icon: LucideIcon;
  text: string;
  /** `link` is tappable (a phone to call); `strong` stands out. */
  tone?: 'link' | 'strong';
  onPress?: () => void;
}

/** A row's facts, wrapped under a hairline; nothing when there are none. */
export const ApdmFactFooter = memo(({ facts }: { facts: ApdmFact[] }) => {
  const { rpx } = useResponsiveContext();
  if (!facts.length) return null;
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: rpx(12),
        rowGap: rpx(4),
        marginTop: rpx(10),
        paddingTop: rpx(8),
        borderTopWidth: 1,
        borderTopColor: aiColors.border,
      }}
    >
      {facts.map(({ key, icon: Icon, text, tone, onPress }) => {
        const color =
          tone === 'link'
            ? aiColors.primary
            : tone === 'strong'
            ? tint(aiColors.foreground, 0.8)
            : aiColors.mutedForeground;
        const content = (
          <>
            <Icon size={rpx(12)} color={color} />
            {tone === 'link' ? (
              <Typography.Text medium size={rpx(11)} color={color}>
                {text}
              </Typography.Text>
            ) : (
              <Typography.Text size={rpx(11)} color={color}>
                {text}
              </Typography.Text>
            )}
          </>
        );
        const style: ViewStyle = {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
        };
        return onPress ? (
          <Pressable
            key={key}
            accessibilityRole="button"
            onPress={onPress}
            hitSlop={6}
            style={style}
          >
            {content}
          </Pressable>
        ) : (
          <View key={key} style={style}>
            {content}
          </View>
        );
      })}
    </View>
  );
});

/**
 * A list row while it loads, laid out as the customer and lead rows (plate
 * and state, a line or two, the fact footer) so the real row takes its
 * place without a shift.
 */
const ApdmRowSkeleton = memo(({ lines }: { lines: DimensionValue[] }) => {
  const { rpx } = useResponsiveContext();
  const bar = (width: DimensionValue, height: number, marginTop = 0) => (
    <View
      style={{
        width,
        height,
        marginTop,
        borderRadius: rpx(6),
        backgroundColor: aiColors.skeleton,
      }}
    />
  );
  return (
    <MotiView
      from={{ opacity: 1 }}
      animate={{ opacity: 0.55 }}
      transition={{ type: 'timing', duration: 800, loop: true }}
      style={{
        padding: rpx(14),
        borderRadius: rpx(14),
        borderWidth: 1,
        borderColor: aiColors.border,
        backgroundColor: aiColors.card,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {bar(rpx(96), rpx(26))}
        {bar(rpx(48), rpx(18))}
      </View>
      {lines.map((width, index) => (
        <View key={index}>{bar(width, rpx(12), rpx(10))}</View>
      ))}
      <View
        style={{
          flexDirection: 'row',
          gap: rpx(12),
          marginTop: rpx(12),
          paddingTop: rpx(8),
          borderTopWidth: 1,
          borderTopColor: aiColors.border,
        }}
      >
        {bar('22%', rpx(11))}
        {bar('16%', rpx(11))}
        {bar('26%', rpx(11))}
      </View>
    </MotiView>
  );
});

// Rows of different lengths, so the list reads as a list while it loads.
const kSkeletonLines: DimensionValue[][] = [
  ['58%', '82%'],
  ['74%'],
  ['46%', '68%'],
  ['64%'],
];

/** A loading list of customer or lead rows. */
export const ApdmListSkeleton = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View accessibilityLabel="加载中" style={{ gap: rpx(10) }}>
      {kSkeletonLines.map((lines, index) => (
        <ApdmRowSkeleton key={index} lines={lines} />
      ))}
    </View>
  );
});

/**
 * An empty list, or one that failed: an icon over a short message, and a
 * retry under a failure, centred in a block tall enough that the tab keeps
 * its body instead of collapsing to one line.
 */
export const ApdmEmptyList = memo(
  ({
    icon: Icon,
    text,
    onRetry,
  }: {
    icon: LucideIcon;
    text: string;
    /** The list failed to load: a 重试 button under the message. */
    onRetry?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          minHeight: rpx(240),
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(12),
          paddingVertical: rpx(24),
        }}
      >
        <View
          style={{
            width: rpx(56),
            height: rpx(56),
            borderRadius: rpx(28),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: aiColors.muted,
          }}
        >
          <Icon size={rpx(26)} color={aiColors.mutedForeground} />
        </View>
        <Typography.Text
          size={rpx(13)}
          color={aiColors.mutedForeground}
          style={{ textAlign: 'center' }}
        >
          {text}
        </Typography.Text>
        {onRetry ? (
          <Pressable
            accessibilityRole="button"
            onPress={onRetry}
            style={({ pressed }) => ({
              paddingHorizontal: rpx(16),
              paddingVertical: rpx(7),
              borderRadius: rpx(10),
              borderWidth: 1,
              borderColor: aiColors.border,
              backgroundColor: aiColors.card,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Typography.Text medium size={rpx(13)} color={aiColors.primary}>
              重试
            </Typography.Text>
          </Pressable>
        ) : null}
      </View>
    );
  },
);
